import { ReactNode, useId, useState } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations/index.ts';

import { AccordionCopyLink } from './accordion-copy-link.tsx';
import { TogglerIcon } from './toggler-icon.tsx';

export interface AccordionProps {
	header: string;
	content: ReactNode | JSX.Element;
	id?: string;
}

export const Accordion = (props: AccordionProps) => {
	const { header, content, id } = props;
	const [isOpen, setIsOpen] = useState(false);
	const headingId = useId();
	const contentId = useId();

	const handleToggle = () => setIsOpen((open) => !open);

	return (
		<div id={id || undefined} css={containerCss}>
			<div css={headerContainerCss}>
				<h3 id={headingId} css={headerCss} className="card-title-sm">
					{header}
				</h3>
				{id && <AccordionCopyLink id={id} header={header} />}
				<div css={togglerContainerCss}>
					<TogglerIcon icon={isOpen ? 'minus' : 'plus'} />
				</div>
				<button
					type="button"
					css={toggleButtonCss}
					aria-expanded={isOpen}
					aria-controls={contentId}
					aria-labelledby={headingId}
					onClick={handleToggle}
				/>
			</div>
			<div id={contentId} css={[contentCss, isOpen && contentOpenCss]} aria-hidden={!isOpen}>
				{content}
			</div>
		</div>
	);
};

const containerCss = css({
	width: '100%',
	borderBlockEnd: `1px solid ${wacToken('color.neutral.light300')}`,
	padding: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover h3': {
		color: wacToken('color.extended.blue800'),
	},
});

const headerContainerCss = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const toggleButtonCss = css({
	position: 'absolute',
	inset: 0,
	width: '100%',
	padding: 0,
	border: 0,
	backgroundColor: 'transparent',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.extended.blue800')}`,
		outlineOffset: token('space.050'),
	},
});

const togglerContainerCss = css({
	flexShrink: 0,
	pointerEvents: 'none',
});

const headerCss = css({
	margin: 0,
	minWidth: 0,
	flexGrow: 1,
	pointerEvents: 'none',
});

const contentCss = css({
	blockSize: 0,
	paddingBlockStart: 0,
	overflowY: 'clip',
	visibility: 'hidden',
	contentVisibility: 'hidden',
	interpolateSize: 'allow-keywords',
	transitionProperty: 'block-size, padding-block-start, visibility, content-visibility',
	transitionDuration: '.25s',
	transitionBehavior: 'allow-discrete',
	transitionTimingFunction: 'ease-in-out',
	'@media (prefers-reduced-motion: reduce)': {
		transitionDuration: '0s',
	},
});

const contentOpenCss = css({
	blockSize: 'auto',
	paddingBlockStart: token('space.200'),
	visibility: 'visible',
	contentVisibility: 'visible',
});
